import moment from 'moment-timezone';
import {
  buildPatternDetails,
  parseGenptRecurrenceUiHint,
  getOrdinalFromStartDate,
  resolveThrottleStrategyForWire,
} from '../Chat/utils/buildGenptSchedulePayload';

const MONTHLY_LABELS = Array.from({ length: 31 }, (_, i) => {
  const d = i + 1;
  let suffix = 'th';
  if (d === 1 || d === 21 || d === 31) suffix = 'st';
  else if (d === 2 || d === 22) suffix = 'nd';
  else if (d === 3 || d === 23) suffix = 'rd';
  return `${d}${suffix}`;
});

/** Same order as `buildGenptSchedulePayload` — used to infer `weeklyPattern: 'matches'` on edit. */
const DAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const LONG_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function longWeekdayFromYmd(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : LONG_WEEKDAYS[d.getDay()];
}

function capitalizeUnit(u) {
  let s = String(u || 'day').toLowerCase();
  if (['days', 'weeks', 'months', 'years'].includes(s)) s = s.slice(0, -1);
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Day';
}

/** HH:mm:ss or HH:mm -> { startHour, startMin, startAmPm } */
export function parseTestStartTimeParts(testStartTime) {
  const raw = String(testStartTime || '09:00:00');
  const [hs, ms] = raw.split(':');
  let h = parseInt(hs, 10);
  const min = ((ms || '00') + '00').slice(0, 2);
  if (Number.isNaN(h)) {
    return { startHour: '09', startMin: '00', startAmPm: 'AM' };
  }
  const ampm = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return {
    startHour: String(h12).padStart(2, '0'),
    startMin: min,
    startAmPm: ampm,
  };
}

function mapWeekRankFromApi(wr) {
  const map = {
    first: 'First',
    second: 'Second',
    third: 'Third',
    fourth: 'Fourth',
    fifth: 'Fifth',
    last: 'Last',
  };
  const k = String(wr || '').toLowerCase();
  return map[k] || 'First';
}

/** When booleans are absent on GET, infer from human `frequency` (server often echoes intent here). */
function inferMonthlyRelativeDayFromFrequencyText(frequencyRaw) {
  const t = String(frequencyRaw || '').toLowerCase();
  if (!t.trim()) return null;
  if (/\bweekends?\b/.test(t)) return 'Weekend';
  if (/\bweekdays?\b/.test(t)) return 'Weekday';
  if (/\bweek\s*days\b/.test(t)) return 'Weekday';
  if (/\b(business|working)\s*days?\b/.test(t)) return 'Weekday';
  return null;
}

function mapRelativeDayTypeEnum(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const x = String(raw).trim().toLowerCase();
  if (['weekday', 'weekdays', 'weekday_only'].includes(x)) return 'Weekday';
  if (['weekend', 'weekends', 'weekend_only'].includes(x)) return 'Weekend';
  if (['day', 'calendar', 'calendar_day', 'any_day', 'all_days'].includes(x)) return 'Day';
  return null;
}

/** Maps GET `pattern_details` (+ optional `frequency`) back to ScheduleTest "Day type".
 *
 * Backend wire shape (per GenPT schedules API doc):
 *  - Weekday → `is_weekday_only: true` (with `week_rank`).
 *  - Weekend → `days_of_week: ['SAT','SUN']` (with `week_rank`); may also carry `is_weekend_only`.
 *  - Nth calendar weekday → single entry in `days_of_week` (e.g. `TUE`) with `week_rank`.
 *  - Day     → encoded via `day_of_month` (1 / -1), not via week_rank.
 * Legacy GETs may still carry `is_weekend_only` / `relative_day_type` — keep the fallbacks. */
function mapPatternDetailsToMonthlyRelativeDay(pd, frequencyRaw) {
  const fromEnum = mapRelativeDayTypeEnum(
    pd?.relative_day_type ??
      pd?.relativeDayType ??
      pd?.day_type ??
      pd?.dayType
  );
  if (fromEnum) return fromEnum;

  const weekdayOnly = !!(pd?.is_weekday_only ?? pd?.isWeekdayOnly);
  const weekendOnly = !!(
    pd?.is_weekend_only ??
      pd?.isWeekendOnly ??
      pd?.weekend_only ??
      pd?.weekendOnly ??
      pd?.is_weekend ??
      pd?.isWeekend
  );
  if (weekdayOnly) return 'Weekday';
  if (weekendOnly) return 'Weekend';

  const dow = Array.isArray(pd?.days_of_week)
    ? pd.days_of_week
    : Array.isArray(pd?.daysOfWeek)
      ? pd.daysOfWeek
      : null;
  if (dow && dow.length > 0) {
    const upper = dow.map((d) => String(d).toUpperCase());
    const isWeekendPair =
      upper.length === 2 && upper.includes('SAT') && upper.includes('SUN');
    if (isWeekendPair) return 'Weekend';
    if (upper.length === 1) {
      const idx = DAYS_SHORT.indexOf(upper[0]);
      if (idx >= 0) return LONG_WEEKDAYS[idx];
    }
  }

  const fromFreq = inferMonthlyRelativeDayFromFrequencyText(frequencyRaw);
  if (fromFreq) return fromFreq;

  return 'Day';
}

function mapEndsTypeUi(endsType) {
  const t = String(endsType || 'never').toLowerCase();
  if (t === 'on_date') return 'on';
  if (t === 'after_occurrences') return 'after';
  return 'never';
}

/** Abbreviations in API strings like "2026-03-24 09:00:00 PDT" → IANA ids used by ScheduleTest dropdown */
const TZ_ABBREV_TO_IANA = {
  PDT: 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  EDT: 'America/New_York',
  EST: 'America/New_York',
  CDT: 'America/Chicago',
  CST: 'America/Chicago',
  MDT: 'America/Denver',
  MST: 'America/Denver',
  UTC: 'UTC',
  GMT: 'UTC',
  IST: 'Asia/Kolkata',
};

/**
 * Parses API field `scheduleStartDateAndTimeWithTimezone`, e.g. "2026-03-24 09:00:00 PDT".
 * Returns { startDate, timeParts, timeZone } or null.
 */
export function parseScheduleStartDateTimeWithTimezone(raw) {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  const re =
    /^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([A-Za-z]{2,5})?$/;
  const m = str.match(re);
  if (!m) return null;
  const [, ymd, h, min, sec, tzAbbr] = m;
  const hour24 = parseInt(h, 10);
  if (Number.isNaN(hour24)) return null;
  const secPart = sec || '00';
  const timeParts = parseTestStartTimeParts(
    `${String(hour24).padStart(2, '0')}:${min}:${secPart}`
  );
  const abbrev = (tzAbbr || 'UTC').toUpperCase();
  const timeZone = TZ_ABBREV_TO_IANA[abbrev] || 'UTC';
  return { startDate: ymd, timeParts, timeZone };
}

/** Maps API `frequency` string, e.g. "Monthly", "Every 1 month" → ScheduleTest repeatUnit label */
function mapFrequencyToRepeatUnitLabel(frequency) {
  const f = String(frequency || '').toLowerCase();
  if (!f) return null;
  if (f === 'once' || /^run\s+only\s+once$/i.test(String(frequency || '').trim())) return null;
  if (f.includes('year')) return 'Year';
  if (f.includes('month')) return 'Month';
  if (f.includes('week')) return 'Week';
  if (f.includes('day')) return 'Day';
  return null;
}

/** True when API human frequency means a single execution (ROAR-4359 / empty repeat_unit). */
function isFrequencyOnceDisplay(frequencyRaw) {
  const f = String(frequencyRaw ?? '').trim().toLowerCase();
  if (!f) return false;
  if (f === 'once') return true;
  if (f === 'run only once' || f === 'run-only-once') return true;
  return false;
}

/** e.g. "Every 2 weeks" → 2 when `repeat_interval` is absent on GET */
function mapFrequencyToRepeatInterval(frequency) {
  const m = String(frequency || '').match(/every\s+(\d+)/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) || n < 1 ? null : String(n);
}

const FREEZE_UI_MINUTE_OPTIONS = ['00', '15', '30', '45'];

function snapMinuteToFreezeUiSelect(mm) {
  const p = String(mm ?? '00').padStart(2, '0');
  return FREEZE_UI_MINUTE_OPTIONS.includes(p) ? p : '00';
}

/** API ISO → ScheduleTest freezing fields (12h + AM/PM; must match buildFreezingPeriods / normalizeTimeSelectParts). */
function mapFreezingToUi(periods) {
  if (!Array.isArray(periods) || periods.length === 0) return [];
  return periods.map((p, idx) => {
    const sRaw = p.startAt ?? p.start_at ?? p.start;
    const eRaw = p.endAt ?? p.end_at ?? p.end;
    const s = sRaw ? moment(sRaw) : null;
    const e = eRaw ? moment(eRaw) : null;
    const sH12 = s?.isValid() ? s.format('hh') : '09';
    const sM = s?.isValid() ? snapMinuteToFreezeUiSelect(s.format('mm')) : '00';
    const sAmpm = s?.isValid() ? (s.hours() >= 12 ? 'PM' : 'AM') : 'AM';
    const sTime = `${sH12}:${sM}`;
    const eH12 = e?.isValid() ? e.format('hh') : '05';
    const eM = e?.isValid() ? snapMinuteToFreezeUiSelect(e.format('mm')) : '00';
    const eAmpm = e?.isValid() ? (e.hours() >= 12 ? 'PM' : 'AM') : 'PM';
    const eTime = `${eH12}:${eM}`;
    return {
      id: idx + 1,
      startDate: s?.isValid() ? s.format('YYYY-MM-DD') : '',
      startTime: sTime,
      startAmPm: sAmpm,
      endDate: e?.isValid() ? e.format('YYYY-MM-DD') : '',
      endTime: eTime,
      endAmPm: eAmpm,
    };
  });
}

/**
 * Maps GET /genpt/schedules/:id (or list row) payload → TestConfigurationForm + ScheduleTest prefill.
 * Returns null if schedule is missing.
 * @param {object} [options]
 * @param {string} [options.scheduleNameHint] — from All Schedules row when API detail omits `name`
 */
export function mapGenptScheduleToEditPrefill(rawSchedule, options = {}) {
  if (!rawSchedule || typeof rawSchedule !== 'object') return null;

  const { scheduleNameHint } = options;
  /** GET detail is often `{ success, schedule: { … throttling_config … } }` — normalize to inner schedule */
  const s =
    rawSchedule.schedule && typeof rawSchedule.schedule === 'object'
      ? rawSchedule.schedule
      : rawSchedule.data && typeof rawSchedule.data === 'object' && rawSchedule.data.schedule
        ? rawSchedule.data.schedule
        : rawSchedule;
  const tc = s.test_config || s.testConfig || {};
  const th = s.throttling_config || s.throttlingConfig || {};

  const targetURL =
    tc.targetURL ?? tc.target_url ?? s.url ?? s.target_url ?? s.targetUrl ?? '';
  const assetId = tc.assetId ?? tc.asset_id ?? s.assetId ?? s.asset_id ?? null;

  const authType = String(tc.authType ?? tc.auth_type ?? 'unauthenticated').toLowerCase();
  const testTypeRaw = String(tc.testType ?? tc.test_type ?? 'normal').toLowerCase();

  /** Saved credential id + display name for schedule edit / TestSetup dropdown (GET shapes vary). */
  const savedCredIdRaw =
    tc.saved_credential ??
    tc.savedCredential ??
    tc.savedCredentialId ??
    tc.saved_credential_id ??
    tc.credential_id ??
    tc.credential?.credential_id ??
    tc.credential?.id ??
    null;
  const savedCredId =
    savedCredIdRaw != null && typeof savedCredIdRaw === 'object'
      ? savedCredIdRaw.credential_id ?? savedCredIdRaw.id ?? null
      : savedCredIdRaw;

  const savedCredNameRaw =
    tc.savedCredentialName ??
    tc.saved_credential_name ??
    tc.credential_name ??
    tc.credential?.credential_name ??
    tc.credential?.name ??
    null;
  const savedCredName =
    savedCredNameRaw != null && String(savedCredNameRaw).trim() !== ''
      ? String(savedCredNameRaw).trim()
      : null;

  const assetTypeFromSchedule =
    tc.asset_type ??
    tc.assetType ??
    s.platform_category ??
    s.platformCategory ??
    null;

  const submitted = {
    targetURL,
    assetId,
    ...(assetTypeFromSchedule != null &&
      String(assetTypeFromSchedule).trim() !== '' && {
        asset_type: String(assetTypeFromSchedule).trim().toLowerCase(),
      }),
    testType:
      testTypeRaw === 'quick'
        ? 'quick'
        : testTypeRaw === 'wordpress'
          ? 'wordpress'
          : testTypeRaw === 'scenario' // ROAR-4511: preserve scenario on edit (was coerced to normal)
            ? 'scenario'
            : 'normal',
    authType: authType === 'authenticated' ? 'authenticated' : 'unauthenticated',
    showAuthFields: authType === 'authenticated',
    /** From `test_config` only — explicit true/false, else derived from `authType`. */
    continueAsUnauth: (() => {
      if (tc.continueAsUnauth === false || tc.continue_as_unauth === false) return false;
      if (tc.continueAsUnauth === true || tc.continue_as_unauth === true) return true;
      return authType !== 'authenticated';
    })(),
    checked: authType === 'authenticated',
    githubAccount: tc.githubAccount ?? tc.github?.accountName ?? null,
    githubRepo: tc.githubRepo ?? tc.github?.repoName ?? null,
    networkType: (tc.networkType || tc.network_type || 'external').toLowerCase() === 'internal'
      ? 'internal'
      : 'external',
    selectedVpnProfile:
      tc.vpnProfileId ?? tc.vpn_profile_id ?? tc.vpnProfile?.id ?? null,
    ...(savedCredId != null && String(savedCredId).trim() !== '' && { savedCredential: savedCredId }),
    ...(savedCredName && { savedCredentialName: savedCredName }),
    loginUrl: tc.loginUrl ?? tc.login_url ?? null,
  };

  /**
   * ROAR-4511: business-scenario schedule — surface the stored scenario so Edit shows the confirmed
   * scenario and the PUT diff re-emits scenario_id (buildTestConfigForSchedule reads these camelCase
   * keys). The schedule test_config stores `scenario: { scenario_id, description, feature_name }` +
   * top-level `scenario_id`; steps live on the genpt_scenarios row, so they may be absent here.
   */
  if (submitted.testType === 'scenario') {
    const scen = tc.scenario && typeof tc.scenario === 'object' ? tc.scenario : {};
    const scenarioIdVal = tc.scenario_id ?? tc.scenarioId ?? scen.scenario_id ?? scen.id ?? null;
    if (scenarioIdVal != null && String(scenarioIdVal).trim() !== '') {
      submitted.scenarioId = scenarioIdVal;
    }
    const descVal = scen.description ?? tc.description ?? null;
    if (descVal != null && String(descVal).trim() !== '') {
      submitted.scenarioDescription = String(descVal);
    }
    const featVal =
      scen.feature_name ?? scen.featureName ?? tc.feature_name ?? tc.featureName ?? null;
    if (featVal != null && String(featVal).trim() !== '') {
      submitted.featureName = String(featVal);
    }
    const stepsVal = Array.isArray(scen.steps)
      ? scen.steps
      : Array.isArray(tc.steps)
        ? tc.steps
        : null;
    if (stepsVal && stepsVal.length > 0) {
      submitted.scenarioSteps = stepsVal;
    }
  }

  /** ROAR-4384: API schedules persist collection on `test_config` — seed schedule modal + PUT comparison. */
  const rawApiFiles = tc.api_files ?? tc.apiFiles;
  if (Array.isArray(rawApiFiles) && rawApiFiles.length > 0) {
    const normalized = rawApiFiles
      .map((f) => {
        const file_key = f?.file_key ?? f?.fileKey;
        if (!file_key) return null;
        const file_name = f?.file_name ?? f?.fileName;
        return file_name ? { file_key, file_name } : { file_key };
      })
      .filter(Boolean);
    if (normalized.length > 0) submitted.api_files = normalized;
  }
  const rawApiUrls = tc.api_collection_urls ?? tc.apiCollectionUrls;
  if (Array.isArray(rawApiUrls) && rawApiUrls.length > 0) {
    const urls = rawApiUrls.map((u) => String(u).trim()).filter(Boolean);
    if (urls.length > 0) submitted.api_collection_urls = urls;
  }
  const schemaEp = tc.schema_endpoint_url ?? tc.schemaEndpointUrl;
  if (schemaEp != null && String(schemaEp).trim() !== '') {
    submitted.schema_endpoint_url = String(schemaEp).trim();
  }

  /**
   * ROAR-4423 / Edit-Schedule API auth prefill: schedules persist API auth as
   * `test_config.cookie_authentication` (string) and `test_config.custom_headers`
   * (`[{ header_name, header_value }, ...]`, one entry per header — up to the form's 5-row cap).
   * `APIConfigurationForm`'s post-mount prefill effect reads camelCase keys
   * (`cookieAuthentication`, `customHeaders[i].headerName/headerValue`) off `submittedData`,
   * which is initialized from this mapper's output. Without this normalization the saved
   * cookie + each header row are silently dropped on Edit. `buildTestConfigForSchedule`
   * destructures the same camelCase keys, so this is also the shape the baseline PUT diff
   * is computed in.
   */
  const rawCookie = tc.cookie_authentication ?? tc.cookieAuthentication;
  if (rawCookie != null && String(rawCookie) !== '') {
    submitted.cookieAuthentication = String(rawCookie);
  }
  const rawHeaders = tc.custom_headers ?? tc.customHeaders;
  if (Array.isArray(rawHeaders) && rawHeaders.length > 0) {
    const normalizedHeaders = rawHeaders
      .map((h) => {
        if (!h || typeof h !== 'object') return null;
        const name = h.headerName ?? h.header_name ?? '';
        const value = h.headerValue ?? h.header_value ?? '';
        const nameStr = typeof name === 'string' ? name : String(name ?? '');
        const valueStr = typeof value === 'string' ? value : String(value ?? '');
        if (!nameStr.trim() && !valueStr.trim()) return null;
        return { headerName: nameStr, headerValue: valueStr };
      })
      .filter(Boolean);
    if (normalizedHeaders.length > 0) {
      submitted.customHeaders = normalizedHeaders;
    }
  }

  const rateLimit = th.rateLimit ?? th.rate_limit ?? 80;
  const concurrency = th.maxConcurrent ?? th.max_concurrent ?? th.concurrency ?? 10;
  const requestTimeout = th.requestTimeout ?? th.request_timeout ?? th.timeout ?? 5;
  const vulnRetries = th.retries ?? 2;

  const valRaw = th.validation && typeof th.validation === 'object' ? th.validation : {};
  const validationTimeout =
    valRaw.timeout ??
    valRaw.request_timeout ??
    requestTimeout;
  const validationRetries =
    valRaw.retries ?? valRaw.retries_count ?? vulnRetries ?? 2;

  const strategy = resolveThrottleStrategyForWire({
    strategy: th.strategy ?? th.throttling_strategy,
    vulnerability: {
      rate_limit: rateLimit,
      concurrency,
      timeout: requestTimeout,
    },
  });

  const config_settings = {
    vulnerability: {
      rate_limit: rateLimit,
      concurrency,
      timeout: requestTimeout,
      retries: vulnRetries,
    },
    validation: {
      timeout: validationTimeout,
      retries: validationRetries,
    },
    strategy,
  };

  const frequencyRaw =
    s.frequency ??
    s.schedule_frequency ??
    s.scheduleFrequency ??
    s.scheduleFrequencyText ??
    s.schedule_frequency_text;

  const recurrenceWire = String(s.recurrence_type || s.recurrenceType || '')
    .trim()
    .toLowerCase();
  /** ROAR-4359: backend may omit `recurrence_type` on GET but set `frequency` to "Once" and `repeat_unit` empty. */
  const recurrenceType =
    recurrenceWire === 'once' || isFrequencyOnceDisplay(frequencyRaw)
      ? 'once'
      : 'custom';

  const repeatUnitFromFrequency = mapFrequencyToRepeatUnitLabel(frequencyRaw);
  /** Prefer wire `repeat_unit` over human `frequency` — display strings can disagree (ROAR-4343). */
  const repeatUnitExplicit =
    s.repeat_unit ??
    s.repeatUnit ??
    s.frequency_unit ??
    s.frequencyUnit ??
    null;
  const repeatUnit =
    repeatUnitExplicit != null && String(repeatUnitExplicit).trim() !== ''
      ? capitalizeUnit(repeatUnitExplicit)
      : repeatUnitFromFrequency || capitalizeUnit('day');
  let repeatValue = String(
    s.repeat_interval ??
      s.repeatInterval ??
      s.interval ??
      s.every_n ??
      s.everyN ??
      '1'
  );
  if (
    (repeatValue === '1' || repeatValue === '0') &&
    frequencyRaw != null &&
    String(frequencyRaw).trim() !== ''
  ) {
    const fromFreq = mapFrequencyToRepeatInterval(frequencyRaw);
    if (fromFreq != null) repeatValue = fromFreq;
  }
  if (recurrenceType === 'once') repeatValue = '1';
  {
    const n = parseInt(repeatValue, 10);
    if (recurrenceType !== 'once' && (Number.isNaN(n) || n < 1)) {
      repeatValue = '1';
    }
  }

  /** ROAR-4382: Year removed from schedule UI — coerce legacy `repeat_unit: year` to months (cap 12, same as Month repeat max). */
  if (String(repeatUnit).toLowerCase() === 'year') {
    const yn = Math.max(1, parseInt(String(repeatValue), 10) || 1);
    repeatUnit = 'Month';
    repeatValue = String(Math.min(12, yn * 12));
  }

  /** Prefer human-readable anchor; else camelCase API fields; else derive from next_run ISO */
  const startStr =
    s.scheduleStartDateAndTimeWithTimezone ||
    s.schedule_start_date_and_time_with_timezone ||
    null;
  let parsedStart = parseScheduleStartDateTimeWithTimezone(startStr);

  if (!parsedStart) {
    const startDateRaw = s.start_date || s.startDate;
    if (startDateRaw) {
      const d = moment.utc(startDateRaw);
      parsedStart = {
        startDate: d.format('YYYY-MM-DD'),
        timeParts: parseTestStartTimeParts(
          s.test_start_time || s.testStartTime || '09:00:00'
        ),
        timeZone: s.timezone || s.timeZone || 'UTC',
      };
    }
  }

  if (!parsedStart) {
    const iso = s.next_run_at || s.original_next_run_at || s.nextRunAt;
    const m = iso ? moment.utc(iso) : null;
    if (m?.isValid()) {
      parsedStart = {
        startDate: m.format('YYYY-MM-DD'),
        timeParts: parseTestStartTimeParts(m.format('HH:mm:ss')),
        timeZone: s.timezone || s.timeZone || 'UTC',
      };
    }
  }

  if (!parsedStart) {
    parsedStart = {
      startDate: moment.utc().format('YYYY-MM-DD'),
      timeParts: parseTestStartTimeParts('09:00:00'),
      timeZone: s.timezone || s.timeZone || 'UTC',
    };
  }

  const { startDate, timeParts, timeZone } = parsedStart;

  const pd = s.pattern_details || s.patternDetails || {};
  const daysOfWeek = Array.isArray(pd.days_of_week) ? pd.days_of_week : pd.daysOfWeek;
  const dom = pd.day_of_month ?? pd.dayOfMonth;
  const weekRank = pd.week_rank ?? pd.weekRank;

  const recurrenceUiHint =
    parseGenptRecurrenceUiHint(pd.genpt_recurrence_ui ?? pd.genptRecurrenceUi) ??
    (() => {
      const legacy = pd.genpt_weekly_ui_pattern ?? pd.genptWeeklyUiPattern;
      if (legacy == null || legacy === '') return null;
      const lm = String(legacy).trim().toLowerCase();
      if (lm === 'matches' || lm === 'specific') return parseGenptRecurrenceUiHint(`week:${lm}`);
      return null;
    })();

  let weeklyPattern = 'specific';
  let selectedDays = Array.isArray(daysOfWeek) && daysOfWeek.length > 0 ? [...daysOfWeek] : ['MON'];
  let monthlyPattern = 'specific_date';
  let monthlySpecificDate = MONTHLY_LABELS[14] || '15th';
  let monthlyRelativePosition = 'First';
  let monthlyRelativeDay = 'Day';

  const dayOfMonthFromStartDate = () => {
    const parts = String(startDate || '').split('-');
    if (parts.length < 3) return null;
    const d = parseInt(parts[2], 10);
    return Number.isNaN(d) ? null : d;
  };

  if (String(repeatUnit).toLowerCase() === 'month') {
    const domNum = dom != null && dom !== '' ? parseInt(String(dom), 10) : NaN;
    /** Backend "Last day of month" encoding (`day_of_month: -1`) → UI relative_day · Last · Day. */
    if (domNum === -1) {
      monthlyPattern = 'relative_day';
      monthlyRelativePosition = 'Last';
      monthlyRelativeDay = 'Day';
    } else if (!Number.isNaN(domNum)) {
      monthlyPattern = 'specific_date';
      monthlySpecificDate = MONTHLY_LABELS[Math.min(31, Math.max(1, domNum)) - 1] || `${domNum}th`;
    } else if (weekRank) {
      monthlyRelativePosition = mapWeekRankFromApi(weekRank);
      monthlyRelativeDay = mapPatternDetailsToMonthlyRelativeDay(pd, frequencyRaw);

      const hasDow = Array.isArray(daysOfWeek) && daysOfWeek.length > 0;
      const wkdy = !!(pd?.is_weekday_only ?? pd?.isWeekdayOnly);
      const wknd = !!(
        pd?.is_weekend_only ??
        pd?.isWeekendOnly ??
        pd?.weekend_only ??
        pd?.weekendOnly ??
        pd?.is_weekend ??
        pd?.isWeekend
      );

      const legacyNthWeekday =
        monthlyRelativeDay === 'Day' &&
        !wkdy &&
        !wknd &&
        !hasDow &&
        startDate &&
        getOrdinalFromStartDate(startDate) === monthlyRelativePosition;

      if (LONG_WEEKDAYS.includes(monthlyRelativeDay)) {
        monthlyPattern = 'matches_relative';
      } else if (legacyNthWeekday) {
        const lw = longWeekdayFromYmd(startDate);
        if (lw) {
          monthlyRelativeDay = lw;
          monthlyPattern = 'matches_relative';
        } else {
          monthlyPattern = 'relative_day';
        }
      } else {
        monthlyPattern = 'relative_day';
      }
    } else {
      const dDom = dayOfMonthFromStartDate();
      if (dDom != null) {
        monthlyPattern = 'specific_date';
        monthlySpecificDate =
          MONTHLY_LABELS[Math.min(31, Math.max(1, dDom)) - 1] || `${dDom}th`;
      }
    }
  }

  if (
    recurrenceUiHint?.unit === 'month' &&
    String(repeatUnit).toLowerCase() === 'month'
  ) {
    monthlyPattern = recurrenceUiHint.mode;
  }

  /**
   * Weekly: `pattern_details.genpt_recurrence_ui` = `week:matches` | `week:specific` when persisted.
   * Legacy: `genpt_weekly_ui_pattern`. If missing, infer single-day vs `startDate` (old behavior).
   */
  if (String(repeatUnit).toLowerCase() === 'week' && Array.isArray(daysOfWeek) && daysOfWeek.length > 0) {
    selectedDays = [...daysOfWeek].map((d) => String(d).toUpperCase());
    if (recurrenceUiHint?.unit === 'week') {
      weeklyPattern = recurrenceUiHint.mode;
    } else if (selectedDays.length === 1 && startDate) {
      const anchor = new Date(`${startDate}T12:00:00`);
      if (!Number.isNaN(anchor.getTime())) {
        const startDow = DAYS_SHORT[anchor.getDay()];
        weeklyPattern =
          selectedDays[0] === String(startDow).toUpperCase() ? 'matches' : 'specific';
      } else {
        weeklyPattern = 'specific';
      }
    } else {
      weeklyPattern = 'specific';
    }
  }

  const endBlock = s.scheduleEndDateOrAfterExecutions ?? s.schedule_end_date_or_after_executions;
  let endsType = 'never';
  let endsOnDate = '2026-12-31';
  let endsAfterCount = '10';

  const clampEndsAfterForUi = (v) => {
    const n = parseInt(String(v ?? '10'), 10);
    if (Number.isNaN(n)) return '10';
    return String(Math.min(999, Math.max(1, n)));
  };
  if (endBlock == null) {
    endsType = mapEndsTypeUi(s.ends_type || s.endsType || 'never');
    if (endsType === 'on' && (s.ends_on_date || s.endsOnDate)) {
      endsOnDate = moment.utc(s.ends_on_date || s.endsOnDate).isValid()
        ? moment.utc(s.ends_on_date || s.endsOnDate).format('YYYY-MM-DD')
        : String(s.ends_on_date || s.endsOnDate);
    }
    if (endsType === 'after') {
      endsAfterCount = String(s.ends_after_count ?? s.endsAfterCount ?? '10');
    }
  } else if (typeof endBlock === 'string') {
    endsType = 'on';
    endsOnDate = moment.utc(endBlock).isValid()
      ? moment.utc(endBlock).format('YYYY-MM-DD')
      : endBlock;
  } else if (typeof endBlock === 'object') {
    const et = String(endBlock.ends_type || endBlock.endsType || '').toLowerCase();
    if (et === 'on_date') {
      endsType = 'on';
      const raw = endBlock.ends_on_date ?? endBlock.endsOnDate;
      endsOnDate = raw
        ? moment.utc(raw).format('YYYY-MM-DD')
        : endsOnDate;
    } else if (et === 'after_occurrences') {
      endsType = 'after';
      endsAfterCount = String(
        endBlock.ends_after_count ?? endBlock.endsAfterCount ?? '10'
      );
    } else {
      endsType = mapEndsTypeUi(s.ends_type || s.endsType || 'never');
      if (s.ends_on_date || s.endsOnDate) {
        endsOnDate = moment.utc(s.ends_on_date || s.endsOnDate).format('YYYY-MM-DD');
      }
      endsAfterCount = String(s.ends_after_count ?? s.endsAfterCount ?? '10');
    }
  } else {
    endsType = mapEndsTypeUi(s.ends_type || s.endsType || 'never');
    if (s.ends_on_date || s.endsOnDate) {
      endsOnDate = moment.utc(s.ends_on_date || s.endsOnDate).format('YYYY-MM-DD');
    }
    endsAfterCount = String(s.ends_after_count ?? s.endsAfterCount ?? '10');
  }

  endsAfterCount = clampEndsAfterForUi(endsAfterCount);

  /**
   * ROAR-4424: ONCE rows persist backend-forced overrides — `ends_type = "after_occurrences"`
   * and `ends_after_count = 1` (also `repeat_unit = null`, `repeat_interval = 0`) — that don't
   * reflect any user intent. Surfacing them as UI defaults would silently make a ONCE → Custom
   * edit "end after 1 occurrence" without the user touching the Ends section. Use UI defaults
   * for ONCE so the Ends section behaves like Create when the user switches to recurring.
   */
  if (recurrenceType === 'once') {
    endsType = 'never';
    endsOnDate = '2026-12-31';
    endsAfterCount = '10';
  }

  const freezingApi = s.freezing_periods || s.freezingPeriods || [];
  const freezingUi = mapFreezingToUi(freezingApi);
  const isFreezingEnabled = freezingUi.length > 0;

  const scheduleName = String(
    [
      s.schedule_name,
      s.scheduleName,
      s.name,
      s.title,
      s.schedule_title,
      s.scheduleTitle,
      s.label,
      scheduleNameHint,
    ].find((x) => x != null && String(x).trim() !== '') ?? ''
  ).trim();

  const scheduleUi = {
    scheduleName,
    timeZone,
    startDate,
    ...timeParts,
    recurrencyType: recurrenceType,
    repeatValue,
    repeatUnit,
    weeklyPattern,
    selectedDays,
    monthlyPattern,
    monthlySpecificDate,
    monthlyRelativePosition,
    monthlyRelativeDay,
    endsType,
    endsOnDate,
    endsAfterCount,
    isFreezingEnabled,
    freezingPeriods:
      isFreezingEnabled && freezingUi.length > 0
        ? freezingUi
        : [
            {
              id: 1,
              startDate: '',
              startTime: '09:00',
              startAmPm: 'AM',
              endDate: '',
              endTime: '05:00',
              endAmPm: 'PM',
            },
          ],
    summary: { first: '-', last: '-', total: '-' },
  };

  submitted.schedule = scheduleUi;
  submitted.config_settings = config_settings;

  return {
    submittedData: submitted,
    throttlingData: config_settings,
  };
}

/** @param {unknown} v */
function normalizeScheduleConflictAnchorYmd(v) {
  if (v == null) return null;
  const s = String(v).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

/**
 * Wall clock on `anchorYmd` in `scheduleUi.timeZone` → UTC start instant (for conflict comparison).
 * @returns {string|null} ISO-8601 UTC
 */
function buildGenptScheduleUtcStartInstant(scheduleUi, anchorYmd) {
  if (!scheduleUi || typeof scheduleUi !== 'object') return null;
  const anchor = normalizeScheduleConflictAnchorYmd(anchorYmd);
  if (!anchor) return null;
  const tz = String(scheduleUi.timeZone ?? '').trim();
  if (!tz || !moment.tz.zone(tz)) return null;
  const h = String(scheduleUi.startHour ?? '09').padStart(2, '0');
  const m = String(scheduleUi.startMin ?? '00').padStart(2, '0');
  const ap = String(scheduleUi.startAmPm ?? 'AM').trim().toUpperCase() || 'AM';
  const local = moment.tz(`${anchor} ${h}:${m} ${ap}`, 'YYYY-MM-DD hh:mm A', tz);
  if (!local.isValid()) return null;
  return local.utc().milliseconds(0).toISOString();
}

function normalizeRepeatUnitForConflictKey(unit) {
  const s = String(unit || 'day').toLowerCase().replace(/s$/, '');
  if (['day', 'week', 'month', 'year'].includes(s)) return s;
  return 'day';
}

/**
 * ROAR-4341: stable recurrency identity — “same time/date” uses UTC instant; “same Recurrency”
 * is this string (run-once date, or custom repeat + pattern details). Must match for a conflict.
 */
function buildGenptScheduleRecurrencySignature(scheduleUi) {
  if (!scheduleUi || typeof scheduleUi !== 'object') return '';
  const rt = String(scheduleUi.recurrencyType ?? '').toLowerCase();
  const startYmd = normalizeScheduleConflictAnchorYmd(scheduleUi.startDate) || '';
  const rv = String(scheduleUi.repeatValue ?? '1').trim() || '1';
  const ru = normalizeRepeatUnitForConflictKey(scheduleUi.repeatUnit);

  if (rt === 'once') {
    return `once|${startYmd}`;
  }

  const parts = [`custom|${rv}|${ru}`];
  if (ru === 'week') {
    /** Canonical weekdays from the same rules as POST `pattern_details` — `matches` vs one-day `specific` stay identical. */
    const pd = buildPatternDetails(scheduleUi);
    const dowList = Array.isArray(pd?.days_of_week) ? pd.days_of_week : [];
    const days = [...dowList].map((d) => String(d).toUpperCase()).sort().join('.');
    parts.push(`d:${days}`);
  } else if (ru === 'month') {
    parts.push(
      `mp:${String(scheduleUi.monthlyPattern || '').toLowerCase()}`,
      `msd:${String(scheduleUi.monthlySpecificDate || '')}`,
      `mrp:${String(scheduleUi.monthlyRelativePosition || '')}`,
      `mrd:${String(scheduleUi.monthlyRelativeDay || '')}`
    );
  } else if (ru === 'year') {
    parts.push('yr:1');
  }
  return parts.join('|');
}

/**
 * ROAR-4341: same-asset schedule conflict when **(1) same start instant in UTC** (anchor day wall
 * time in IANA `timeZone`) **and (2) same recurrency** (run once + date, or same custom pattern).
 * @param {object} scheduleUi - shape like `submittedData.schedule` from this mapper
 * @param {string} [anchorFallbackYmd] - e.g. draft `startDate` when `scheduleUi.startDate` is missing
 * @returns {string|null} composite key, or null if the instant cannot be built
 */
export function buildGenptScheduleTimeConflictKey(scheduleUi, anchorFallbackYmd) {
  if (!scheduleUi || typeof scheduleUi !== 'object') return null;
  const anchor =
    normalizeScheduleConflictAnchorYmd(scheduleUi.startDate) ||
    normalizeScheduleConflictAnchorYmd(anchorFallbackYmd);
  if (!anchor) return null;
  const utc = buildGenptScheduleUtcStartInstant(scheduleUi, anchor);
  if (!utc) return null;
  const rec = buildGenptScheduleRecurrencySignature(scheduleUi);
  return `${utc}|${rec}`;
}

/**
 * List/detail row from GET /genpt/schedules → {@link buildGenptScheduleTimeConflictKey}.
 * @param {string} [anchorFallbackYmd] - draft start date when comparing to the form
 */
export function getGenptScheduleTimeConflictKeyFromListRow(rawSchedule, anchorFallbackYmd) {
  if (!rawSchedule || typeof rawSchedule !== 'object') return null;
  try {
    const pre = mapGenptScheduleToEditPrefill(rawSchedule, { scheduleNameHint: '' });
    if (!pre?.submittedData?.schedule) return null;
    return buildGenptScheduleTimeConflictKey(pre.submittedData.schedule, anchorFallbackYmd);
  } catch {
    return null;
  }
}

export default mapGenptScheduleToEditPrefill;
